import {X} from "react-feather";
import {sidebar} from "navigation";
import {useLayoutEffect} from "react";
import {Overlap} from "components/@helper";
import {useTheme} from "styled-components";
import {useLocation} from "react-router-dom";
import {IconButton} from "components/@button";
import {Frame, Container, NavItem} from "./styled";
import {useAppDispatch, useAppSelector} from "hooks";
import {AnimatePresence, motion} from "framer-motion";
import {buildAvatarPlaceholder, storage} from "utils";
import {toggleMenu, toggleMobileMenu} from "global/slices";
import {
  Row,
  Line,
  Avatar,
  Column,
  Absolute,
  Typography,
} from "components/@system";

interface MobileSidebarProps {}

export const MobileSidebar: React.FC<MobileSidebarProps> = () => {
  const {pathname} = useLocation();
  const dispatch = useAppDispatch();
  const {user} = useAppSelector("auth");
  const {showMobileMenu} = useAppSelector("layout");
  const {name} = user;
  const {black05, white05, isDark, primary, black, blackWhite} = useTheme();

  useLayoutEffect(() => {
    const expanded = storage["read"]("[menu-expanded]");
    if (typeof expanded !== "boolean") dispatch(toggleMenu(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AnimatePresence>
      <Overlap
        onClick={() => {
          dispatch(toggleMobileMenu(false));
        }}
      />
      {showMobileMenu && (
        <Container
          exit={{transform: "translate(-30%, -50%)"}}
          animate={{transform: "translate(2%, -50%)"}}
          initial={{transform: "translate(-30%, -50%)"}}
        >
          <Absolute
            top={12}
            zIndex={7}
            right={showMobileMenu ? 8 : "50%"}
            transform={`translateX(${showMobileMenu ? 0 : "50%"})`}
          >
            <IconButton
              size={35}
              icon={<X strokeWidth={2} size={25} color={blackWhite} />}
              onClick={() => {
                dispatch(toggleMobileMenu(false));
              }}
            />
          </Absolute>
          <Column gap={0}>
            <Row gap={10} width="100%">
              <Frame
                children={
                  <Avatar
                    size={40}
                    borderRadius={8}
                    src={buildAvatarPlaceholder(name.split(" ")[0])}
                  />
                }
              />
              <Typography
                fontSize={18}
                className="line-clamp-1"
                fontFamily="Rubik-Medium"
              >
                {name}
              </Typography>
            </Row>
            <Line margin="1rem 0" />
            {sidebar.map(({module, routes}, key) => {
              return (
                <Column key={key} marginBottom={15}>
                  {module && showMobileMenu && (
                    <motion.div
                      initial={{opacity: 0}}
                      style={{padding: "0 0.5rem"}}
                      animate={{opacity: 1, transition: {delay: 0.2}}}
                    >
                      <Row>
                        <Typography
                          fontSize={12}
                          letterSpacing={1.2}
                          textTransform="uppercase"
                          fontFamily="Rubik-SemiBold"
                          color={isDark ? white05 : black05}
                        >
                          {module["label"]}
                        </Typography>
                      </Row>
                    </motion.div>
                  )}
                  <Column gap={2}>
                    {routes.map(({label, icon: Icon, path}, key) => {
                      const highlighted = pathname.includes(path);

                      return (
                        <NavItem
                          to={path}
                          key={key}
                          onClick={() => {
                            dispatch(toggleMobileMenu(false));
                          }}
                          style={{
                            width: showMobileMenu ? "100%" : "auto",
                            backgroundColor: highlighted ? primary : undefined,
                          }}
                        >
                          <Row gap={10}>
                            <Icon
                              size={20}
                              strokeWidth={2}
                              style={{
                                fontSize: 20,
                                strokeWidth: 2,
                                color: highlighted ? black : blackWhite,
                              }}
                            />
                            {showMobileMenu && (
                              <motion.div
                                style={{padding: "0 0.5rem"}}
                                initial={{
                                  opacity: 0,
                                  transform: "translateX(15px)",
                                }}
                                animate={{
                                  opacity: 1,
                                  transition: {delay: 0.1},
                                  transform: "translateX(0px)",
                                }}
                              >
                                <Typography
                                  fontFamily="Rubik-Medium"
                                  color={highlighted ? black : blackWhite}
                                >
                                  {label}
                                </Typography>
                              </motion.div>
                            )}
                          </Row>
                        </NavItem>
                      );
                    })}
                  </Column>
                </Column>
              );
            })}
          </Column>
        </Container>
      )}
    </AnimatePresence>
  );
};
